import { ReactNode } from 'react';
import camelCase from 'lodash/camelCase';
import { formatPriceRange } from 'dash-common';
import {
  ExpandableText,
  formatBathroomsRangeValues,
  formatBedroomsRangeValues,
} from 'dash-ui';

export enum PropertyCompareItemsEnum {
  StartingPrice = 'StartingPrice',
  NumberOfBeds = 'NumberOfBeds',
  NumberOfBaths = 'NumberOfBaths',
  Market = 'Market',
  AvailableUnits = 'AvailableUnits',
  TaxInfo = 'TaxInfo',
  BuildingTypes = 'BuildingTypes',
  ProjectStatus = 'ProjectStatus',
  YearBuilt = 'YearBuilt',
}

export const COMPARISON_DROPDOWN_OPTIONS: Record<
  PropertyCompareItemsEnum,
  string
> = {
  [PropertyCompareItemsEnum.StartingPrice]: 'forms:labels.startingPrice',
  [PropertyCompareItemsEnum.NumberOfBeds]: 'tables:propertyComparison.bedrooms',
  [PropertyCompareItemsEnum.NumberOfBaths]:
    'tables:propertyComparison.bathrooms',
  [PropertyCompareItemsEnum.Market]: 'forms:labels.marketArea',
  [PropertyCompareItemsEnum.AvailableUnits]:
    'tables:propertyComparison.availableUnits',
  [PropertyCompareItemsEnum.TaxInfo]: 'forms:labels.taxInformation',
  [PropertyCompareItemsEnum.BuildingTypes]: 'forms:labels.buildingType',
  [PropertyCompareItemsEnum.ProjectStatus]: 'tables:propertyComparison.status',
  [PropertyCompareItemsEnum.YearBuilt]: 'forms:labels.yearBuilt',
};

export const DEFAULT_COMPARISON_TABLE_COLUMNS: Record<
  PropertyCompareItemsEnum,
  boolean
> = {
  [PropertyCompareItemsEnum.Market]: true,
  [PropertyCompareItemsEnum.StartingPrice]: true,
  [PropertyCompareItemsEnum.NumberOfBeds]: true,
  [PropertyCompareItemsEnum.NumberOfBaths]: true,
  [PropertyCompareItemsEnum.AvailableUnits]: false,
  [PropertyCompareItemsEnum.YearBuilt]: false,
  [PropertyCompareItemsEnum.TaxInfo]: false,
  [PropertyCompareItemsEnum.BuildingTypes]: false,
  [PropertyCompareItemsEnum.ProjectStatus]: false,
};
//t is the translation function, usually the default useTranslation
export const PROPERTY_COMPARISON_TABLE_COLUMNS: (t: any) => Record<
  PropertyCompareItemsEnum,
  {
    title: string;
    accessor: string;
    Cell?: (options: { row: { original: any } }) => ReactNode;
  }
> = (t) => ({
  [PropertyCompareItemsEnum.Market]: {
    title: 'forms:labels.marketArea',
    accessor: 'marketArea.name',
    Cell: ({ row: { original } }) => {
      return original.marketArea?.name;
    },
  },
  [PropertyCompareItemsEnum.StartingPrice]: {
    title: 'forms:labels.startingPrice',
    accessor: 'priceRange',
    Cell: ({ row }) => {
      const { min } = row.original.priceRange || {};

      if (min) {
        return formatPriceRange(min, undefined, {
          shortMin: (min || 0) % 10 === 0,
          shortMax: false,
          prefix: t('common:labels.from') || '',
        });
      }

      return t('common:labels:noValue');
    },
  },
  [PropertyCompareItemsEnum.NumberOfBeds]: {
    title: 'tables:propertyComparison.bedrooms',
    accessor: 'averageBedroomsNumberValue',
    Cell: ({ row: { original } }) => {
      const { averageBedroomsNumberValue } = original;

      return (
        formatBedroomsRangeValues(averageBedroomsNumberValue, {
          isNullable:
            averageBedroomsNumberValue?.min === 0 &&
            !averageBedroomsNumberValue?.max,
          t,
        }) || t('common:labels:noValue')
      );
    },
  },
  [PropertyCompareItemsEnum.NumberOfBaths]: {
    title: 'tables:propertyComparison.bathrooms',
    accessor: 'averageBathroomsNumberValue',
    Cell: ({ row: { original } }) => {
      const { averageBathroomsNumberValue } = original;

      return (
        formatBathroomsRangeValues(averageBathroomsNumberValue) ||
        t('common:labels:noValue')
      );
    },
  },
  [PropertyCompareItemsEnum.AvailableUnits]: {
    title: 'tables:propertyComparison.availableUnits',
    accessor: 'numberOfUnits',
  },
  [PropertyCompareItemsEnum.BuildingTypes]: {
    title: 'forms:labels.buildingType',
    accessor: 'buildingTypes',
    Cell: ({ row: { original } }) => {
      return !!original.buildingTypes.length
        ? t(
            `forms:labels.buildingTypes.${camelCase(
              original.buildingTypes || ''
            )}`
          )
        : t('common:labels:noValue');
    }
  },
  [PropertyCompareItemsEnum.ProjectStatus]: {
    title: 'tables:propertyComparison.status',
    accessor: 'projectStatus',
    Cell: ({ row: { original } }) =>
      original.projectStatus
        ? t(
            `forms:labels.projectStatuses.${camelCase(
              original.projectStatus || ''
            )}`
          )
        : t('common:labels:noValue'),
  },
  [PropertyCompareItemsEnum.TaxInfo]: {
    title: 'forms:labels.taxInformation',
    accessor: 'taxInformation',
    Cell: ({ row: { original } }) => (
      <ExpandableText lineClamp={1} withButton={false}>
        {original.taxInformation}
      </ExpandableText>
    ),
  },
  [PropertyCompareItemsEnum.YearBuilt]: {
    title: 'forms:labels.yearBuilt',
    accessor: 'yearBuilt',
    Cell: ({ row: { original } }) =>
      original.yearBuilt || t('common:labels:noValue'),
  },
});
